import { useEffect, FC, useRef } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Paper, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CatchNews from '../../../Models/ReportModels/CatchNews';
import Landing from '../../../Models/LandingModels/Landing';
import { getLandingById } from '../../../Services/LondunarkerfiAPIService';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { FilterPayload } from './Filters/FilterDefinition';

interface CatchNewsReportProps {
  apiRef: React.MutableRefObject<GridApiCommunity>;
  toggleDashboardOpen: (open: boolean) => void;
  onLandingSelect: (landing: Landing, newLandingId?: number, landings?: Landing[], newWeightNoteId?: number) => void;
  dateFrom: string;
  setDateFrom: (date: string) => void;
  dateTo: string;
  setDateTo: (date: string) => void;
  loading: boolean;
  catchNews: CatchNews[];
  hasMore: boolean;
  page: number;
  setPage: (page: number | ((prev: number) => number)) => void;
  fetchCatchNews: (
    pageToFetch: number,
    reset: boolean,
    filterModel?: FilterPayload,
    dateFrom?: string,
    dateTo?: string
  ) => Promise<void>;
}

const formatDate = (date: dayjs.Dayjs) => date.format('DD.MM.YYYY');

const CatchNewsReport: FC<CatchNewsReportProps> = (props) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const handleRowClick = async (row: CatchNews) => {
    const landing = await getLandingById(row.landingId);
    props.onLandingSelect(landing, landing.id, undefined, undefined);
    props.toggleDashboardOpen(false);
  };

  // Custom infinite scroll handler
  useEffect(() => {
    const gridNode = gridRef.current;
    if (!gridNode) return;

    const handleScroll = () => {
      if (props.loading || !props.hasMore) return;
      const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
      if (!gridWindow) return;
      const { scrollTop, scrollHeight, clientHeight } = gridWindow as HTMLElement;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        props.setPage(prev => prev + 1);
      }
    };

    const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
    if (gridWindow) {
      gridWindow.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (gridWindow) {
        gridWindow.removeEventListener('scroll', handleScroll);
      }
    };
  }, [props]);

  const columns: GridColDef[] = [
    {
      field: 'landingId',
      headerName: 'Löndunarnúmer',
      flex: 0.7,
      minWidth: 80,
      sortable: true,
    },
    {
      field: 'gearName',
      headerName: 'Veiðarfæri',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'shipRegistrationNumber',
      headerName: 'Skipaskrárnúmer',
      flex: 1,
      minWidth: 80,
      sortable: true,
    },
    {
      field: 'shipName',
      headerName: 'Skip heiti',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'fishName',
      headerName: 'Tegund',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'countFish',
      headerName: 'Magn',
      flex: 1,
      minWidth: 120,
      sortable: true,
      filterable: false,
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="is">

      <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <DatePicker
              label="Frá"
              value={dayjs(props.dateFrom, 'DD.MM.YYYY')}
              onChange={date => {
                if (date) props.setDateFrom(formatDate(date));
              }}
              disableFuture
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 }
                }
              }}
            />
            <DatePicker
              label="Til"
              value={dayjs(props.dateTo, 'DD.MM.YYYY')}
              onChange={date => {
                if (date) props.setDateTo(formatDate(date));
              }}
              disableFuture
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 }
                }
              }}
            />
          </Stack>
        </Box >
        <Box sx={{ flexGrow: 1, height: '100%', minHeight: '400px' }}>
          <div ref={gridRef} style={{ height: '100%' }}>
            <DataGrid
              apiRef={props.apiRef}
              rows={props.catchNews}
              columns={columns}
              loading={props.loading}
              density="standard"
              getRowHeight={() => 'auto'}
              disableColumnFilter
              initialState={{
                sorting: {
                  sortModel: [{ field: 'id', sort: 'desc' }],
                },
              }}
              onRowClick={(params) => {
                handleRowClick(params.row);
              }}
              sx={{
                '& .MuiDataGrid-row:hover': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-paper': {
                  minWidth: 380,
                  maxWidth: 420,
                },
                '& .MuiDataGrid-filterForm': {
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  width: '100%',
                  gap: 2,
                },
              }}
              localeText={{
                toolbarFilters: "Síur",
                toolbarFiltersTooltipHide: "Fela síur",
                toolbarFiltersTooltipShow: "Sýna síur",
                columnHeaderFiltersLabel: "Sía eftir",
                filterOperatorContains: "Inniheldur",
                filterOperatorEquals: "Jafnt og",
                filterOperatorStartsWith: "Byrjar á",
                filterOperatorEndsWith: "Endar á",
                filterOperatorIs: "Er",
                filterOperatorNot: "Er ekki",
                filterOperatorIsEmpty: "Er tómt",
                filterOperatorIsNotEmpty: "Er ekki tómt",
                filterValueAny: "(einhver)",
                filterValueTrue: "satt",
                filterValueFalse: "ósatt",
                filterOperatorIsAnyOf: "Er einhver af",
                toolbarColumns: "Dálkar",
                toolbarColumnsLabel: "Dálkar",
                columnsPanelTextFieldLabel: "Finna dálk",
                columnsPanelTextFieldPlaceholder: "Leita að dálki",
                columnsPanelDragIconLabel: "Endurraða dálkum",
                columnsPanelShowAllButton: "Sýna alla",
                columnsPanelHideAllButton: "Fela alla",
                filterPanelColumns: "Dálkar",
                filterPanelOperator: "Skilyrði",
                filterPanelInputLabel: "Gildi",
                filterPanelInputPlaceholder: "Sía gildi",
                filterPanelAddFilter: "Bæta við síu",
                filterPanelDeleteIconLabel: "Eyða",
                columnMenuLabel: "Valmynd",
                columnMenuShowColumns: "Sýna dálka",
                columnMenuFilter: "Sía",
                columnMenuHideColumn: "Fela",
                columnMenuUnsort: "Hætta að raða",
                columnMenuSortAsc: "Raða hækkandi",
                columnMenuSortDesc: "Raða lækkandi",
                columnMenuManageColumns: "Stjórna dálkum",
              }}
            />
          </div>
        </Box>
      </Paper >
    </LocalizationProvider>
  );
};

export default CatchNewsReport;